import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import { Add, Edit } from '@mui/icons-material'
import { Box, Button, Card, CardActions, CardContent, Chip, Link, Stack, Tooltip, Typography } from '@mui/material'

import { InternalGameScreenshot } from '../../../../api/screenshot-links'
import { getAnalysisOverviewPath } from '../../../analysis/helpers/analysisPaths'
import { getScreenshotAnalysisId } from '../../helpers/implementationExampleHelpers'
import { getScreenshotCommentEditPath } from '../../helpers/implementationExamplePaths'
import { ScreenshotThumbnail } from '../ScreenshotThumbnail/ScreenshotThumbnail'

type ImplementationScreenshotCardProps = {
  screenshot: InternalGameScreenshot
  choice?: string
  gameId: string
  marketIso: string
}

export const ImplementationScreenshotCard: FC<ImplementationScreenshotCardProps> = ({ screenshot, choice, gameId, marketIso }) => {
  const { t } = useTranslation()
  const analysisId = getScreenshotAnalysisId(screenshot, marketIso)
  const image = (
    <ScreenshotThumbnail
      url={screenshot.url}
      alt={t('internal-analysts:implementation_screenshot_alt', { version: screenshot.gameVersion ?? '---' })}
      height={200}
    />
  )
  const hasComment = !!screenshot.commentId
  const commentLabel = t(hasComment ? 'internal-analysts:implementation_comment_edit' : 'internal-analysts:implementation_comment_add')

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }} data-testid={`screenshot-${screenshot.id}`}>
      {analysisId ? (
        <Tooltip title={t('internal-analysts:implementation_open_analysis')}>
          <Link
            href={getAnalysisOverviewPath(analysisId)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('internal-analysts:implementation_open_analysis')}
          >
            {image}
          </Link>
        </Tooltip>
      ) : (
        <Tooltip title={t('internal-analysts:implementation_no_analysis')}>{image}</Tooltip>
      )}
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Stack spacing={0.5}>
          {choice && <Typography variant="body2">{choice}</Typography>}
          <Typography variant="body2" color="text.secondary">
            {t('internal-analysts:comment_version', { version: screenshot.gameVersion ?? '---' })}
          </Typography>
          {hasComment && (
            <Box>
              <Chip
                size="small"
                color={screenshot.commentPublished ? 'success' : 'default'}
                label={t(
                  screenshot.commentPublished ? 'internal-analysts:implementation_comment_published' : 'internal-analysts:implementation_comment_unpublished'
                )}
              />
            </Box>
          )}
        </Stack>
      </CardContent>
      <CardActions>
        <Button
          size="small"
          startIcon={hasComment ? <Edit /> : <Add />}
          component={RouterLink}
          to={getScreenshotCommentEditPath({ gameId, screenshotIds: [screenshot.id], marketIso })}
        >
          {commentLabel}
        </Button>
      </CardActions>
    </Card>
  )
}
